import { Box } from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import { useTranslate } from '@tolgee/react'
import { ButtonOption, OptionsButton } from '@tom-ui/ui'
import { PATHS } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { useNavigate } from 'react-router'
import { HoldTypesViewStore } from '../stores/HoldTypesViewStore'

interface Props {
  item: { id: number; isDefault: boolean }
  viewStore: HoldTypesViewStore
}

export const HoldTypeRowActions = observer(({ item, viewStore }: Props) => {
  const { t } = useTranslate()
  const navigate = useNavigate()
  const { appViewStore } = usePlanningStore()

  const handleRemove = async () => {
    const isConfirmed = await appViewStore.setOpenConfirmDialog(
      true,
      t('removeHoldTypeConfirmation', 'This hold type will be permanently removed.'),
      t('removeHoldType', 'Remove hold type?'),
      undefined,
      {
        primaryActionText: t('yesRemove', 'Yes, remove'),
        secondaryActionText: t('cancel', 'Cancel'),
      },
    )

    if (isConfirmed) {
      try {
        await viewStore.remove(item.id)
        await viewStore.fetch()
        appViewStore.setShowAlert(
          'success',
          t('holdTypeRemovedSuccessfully', 'Hold type removed successfully'),
        )
      } catch (error: any) {
        const errorCode = error?.response?.data?.errorCode
        if (errorCode === 'CannotDeleteHoldTypeWithActiveHolds') {
          appViewStore.setShowAlert(
            'error',
            t('cannotDeleteHoldTypeWithActiveHolds', 'Cannot delete hold type with active holds'),
          )
        } else {
          appViewStore.setShowAlert(
            'error',
            t('failedToRemoveHoldType', 'Failed to remove hold type'),
          )
        }
      }
    }
  }

  const options: ButtonOption[] = [
    {
      label: t('edit', 'Edit'),
      onClick: () => navigate(PATHS.masterData.holdTypes.edit.url.replace(':id', String(item.id))),
      testId: 'edit-hold-type-btn',
    },
    {
      label: t('remove', 'Remove'),
      onClick: handleRemove,
      testId: 'remove-hold-type-btn',
      disabled: item.isDefault,
    },
  ]

  return (
    <Box onClick={e => e.stopPropagation()}>
      <OptionsButton
        tooltip={t('moreOptions', 'More options')}
        options={options}
        position='right'
        testId='hold-type-options-btn'
      />
    </Box>
  )
})
